/**
 *
 * VerifyLogoUpload
 *
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { messages } from './messages';
import * as Yup from 'yup';
import { media } from 'styles/media';
import { useAdvisor } from 'app/Provider/AdvisorProvider';

interface Props {
  onsubmit(values: any): void;
}

// // Main Component
export const LogoUploadForm = forwardRef((props: Props, ref) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileName, setImageFileName] = useState('');
  const [imageFileSelected, setImageFileSelected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setTrackSubmission } = useAdvisor();
  const { setFile, advisorData, setFileName, resetFile, resetFileName } =
    useAdvisor();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const validationSchema = Yup.object().shape({
    image_file: Yup.mixed().test(
      'fileSize',
      'File size must be between 10KB and 200KB',
      value => {
        if (!value) return true; // Return true if no file is provided
        if (!(value instanceof File)) return false;
        const fileSizeKB = value.size / 1024;
        return fileSizeKB >= 10 && fileSizeKB <= 200;
      },
    ),
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit();
    },
  }));
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    validationSchema
      .validate({ image_file: file }, { abortEarly: false })
      .then(() => {
        setImageFile(file);
        setImageFileName(file.name);
        setImageFileSelected(true);
        setError(null);
      })
      .catch(err => {
        setError(err.errors[0]);
        setImageFile(null);
        setImageFileName('');
        setImageFileSelected(false);
      });
  };

  const handleRemoveFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setImageFile(null);
    setImageFileName('');
    setImageFileSelected(false);
    setError(null);
    resetFile();
    resetFileName();
  };

  const handleSubmit = async () => {
    // Remove the file requirement check
    try {
      if (imageFile) {
        const formData = new FormData();
        formData.append('file_type', 'KYC');
        formData.append('files', imageFile);
        setFile(imageFile);
        setFileName(imageFileName);
      }
      setTrackSubmission('imageUpload', true);
      props.onsubmit(imageFile ? new FormData() : null);
    } catch (error) {
      console.error('Error during image upload:', error);
    }
  };
  useEffect(() => {
    if (advisorData?.individualdata.imageFile) {
      setImageFile(advisorData?.individualdata.imageFile);
      setImageFileName(advisorData.individualdata.imageFileName);
      setImageFileSelected(true);
      setError(null);
    }
  }, [
    advisorData.individualdata.imageFile,
    advisorData.individualdata.imageFileName,
  ]);
  return (
    <Div>
      <form onSubmit={e => e.preventDefault()}>
        <Text>
          Add a profile image for clients to trust easily and increase your
          conversion rate.
        </Text>
        <Cover
          className="d-flex justify-content-between align-items-center"
          business={false}
          hasError={!!error}
          onClick={() => !imageFileSelected && fileInputRef.current?.click()}
          style={{ cursor: !imageFileSelected ? 'pointer' : 'default' }}
        >
          {/* <Cover className="d-flex align-items-center" business={false} hasError={!!error}> */}
          <div
            className="d-flex align-items-center"
            style={{
              gap: '15px',
              flex: '1 1 auto',
              minWidth: '0',
              overflow: 'hidden',
            }}
          >
            <img
              style={{ width: '19px', height: '19px', alignSelf: 'center' }}
              src={
                imageFileSelected
                  ? `${process.env.REACT_APP_CDN_URL}/advisor/advisorVerify/uploaded.svg`
                  : `${process.env.REACT_APP_CDN_URL}/advisor/advisorVerify/upload.svg`
              }
              alt="not-found"
            />
            {imageFileSelected ? (
              <PlaceHolder>{imageFileName}</PlaceHolder>
            ) : (
              <PlaceHolder>Upload your profile image</PlaceHolder>
            )}
          </div>
          <div className="d-flex align-items-center" style={{ gap: '10px' }}>
            <img
              style={{ width: '16px', height: '16px', alignSelf: 'center' }}
              src={
                imageFileSelected
                  ? `${process.env.REACT_APP_CDN_URL}/advisor/advisorVerify/removeFile.svg`
                  : `${process.env.REACT_APP_CDN_URL}/advisor/advisorDashboard/uploadBtn.svg`
              }
              alt="Add logo"
              className="cursor-pointer"
              onClick={
                imageFileSelected
                  ? handleRemoveFile
                  : () => fileInputRef.current?.click()
              }
            />
            {!imageFileName && (
              <Label className="d-none d-md-flex">UPLOAD IMAGE</Label>
            )}
          </div>
          {error && <Error>{error}</Error>}
        </Cover>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          id="logo_proof_file"
          name="image_file"
          accept=".png,.jpg,.jpeg,.pdf"
          onChange={handleFileChange}
        />
        <div
          className="d-flex flex-column"
          style={{ gap: '10px', marginTop: '15px' }}
        >
          <SmallText>Size: 10 KB - 200 KB</SmallText>
          <SmallText>Format: png, jpg, jpeg, pdf</SmallText>
          <SmallText>Dimensions: 150 px X 40 px max</SmallText>
        </div>
        <Button type="button" onClick={handleSubmit}>
          Continue
        </Button>
      </form>
    </Div>
  );
});

export default LogoUploadForm;
const Button = styled.button`
  height: 52px;
  background-color: #8276f4;
  padding: 15px 30px;
  border-radius: 30px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  &:hover {
    background-color: #5c54ad;
  }
  ${media.medium} {
    position: absolute;
    bottom: 20px;
    right: 0;
  }
  ${media.large} {
    position: absolute;
    bottom: 20px;
    right: 0;
  }
  ${media.xlarge} {
    position: absolute;
    bottom: 20px;
    right: 0;
  }
`;
const Text = styled.div`
  width: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 500;
  margin-top: 20px;
  ${media.small} {
    font-size: 14px;
    font-weight: 500;
  }
`;
const PlaceHolder = styled.h6`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  ${media.small} {
    font-weight: 500;
  }
  width: 100%;
  overflow: hidden;
  white-space: nowrap; // Prevent text from wrapping
  text-overflow: ellipsis; // Add ellipsis when text overflows
  flex-shrink: 1; // Allow the component to shrink
  min-width: 0; // Allow the text to truncate
`;
const Div = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  // overflow-y:auto;
  ${media.small} {
    height: auto;
  }
`;

const Label = styled.h6`
  font-family: Nunito;
  font-weight: 700;
  font-size: 12px;
  line-height: 16.37px;
  letter-spacing: 10%;
  text-align: center;
  color: #8276f4;
  margin: 0;
  white-space: no wrap;
  flex-shrink: 0;
`;
const Error = styled.div`
  color: #ff7b6a;
  font-weight: 400;
  font-size: 10px;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  white-space: nowrap;
  max-width: 100%;
  line-height: 1.2;
`;
const SmallText = styled.h6`
  font-family: Nunito;
  font-weight: 600;
  font-size: 12px;
  line-height: 16.37px;
  letter-spacing: 0%;
  margin: 0;
`;

const Cover = styled.div<{ business: boolean }>`
  border: 2px solid
    ${props =>
      props.hasError ? '#FFC6BF' : props.business ? ' #E4E6EC' : ' #C6C0FA'};
  height: 52px;
  width: 100%;
  border-radius: 10px;
  background: #ffffff;
  position: relative;
  padding-top: 16px;
  padding-right: 10px;
  padding-bottom: 16px;
  padding-left: 20px;
  margin-top: 30px;
  ${media.small} {
    padding-right: 20px;
  }
`;
